import { useUser } from '@/hooks/use-api'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/lib/utils'
import { getUserTypeDisplayName } from '@/lib/userTypeDisplay'
import { GRACKLE_COLORS, CATEGORICAL, SEMANTIC } from '@/lib/theme'
import {
  User as UserIcon,
  Shield,
  Mail,
  Phone,
  Building,
  Calendar,
  Clock,
  Hash,
  Users,
  Briefcase,
  CheckCircle,
  XCircle,
} from 'lucide-react'

interface UserDetailModalProps {
  userId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Map user_type to categorical color family for role badges.
 * Matches Users page convention.
 */
function getRoleBadgeColor(userType: string): { solid: string; tint: string } {
  const normalized: string = userType.toLowerCase().replace(/\s+/g, '')
  switch (normalized) {
    case 'superadmin':
      return CATEGORICAL.prussian
    case 'admin':
      return CATEGORICAL.navy
    case 'user':
      return CATEGORICAL.teal
    case 'support':
      return CATEGORICAL.clay
    case 'bdr':
      return CATEGORICAL.plum
    case 'orders':
      return CATEGORICAL.steel
    case 'quotes':
      return CATEGORICAL.slate
    case 'rfp':
      return CATEGORICAL.evergreen
    case 'employee':
      return CATEGORICAL.powder
    default:
      return CATEGORICAL.slate // fallback
  }
}

export function UserDetailModal({ userId, open, onOpenChange }: UserDetailModalProps) {
  const { data: user, isLoading, error } = useUser(userId || '')

  if (error) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent aria-describedby="user-error">
          <DialogHeader>
            <DialogTitle>Error Loading User</DialogTitle>
          </DialogHeader>
          <div id="user-error" role="alert" className="text-center py-4 text-muted-foreground">
            Failed to load user details. Please try again.
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  if (isLoading || !user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent aria-describedby="user-loading">
          <DialogHeader>
            <DialogTitle>Loading User...</DialogTitle>
          </DialogHeader>
          <div id="user-loading" role="status" className="text-center py-8 text-muted-foreground">
            Loading user details...
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-y-auto"
        aria-describedby="user-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {user.user_type === 'Admin' ? (
              <Shield
                className="w-5 h-5"
                style={{ color: GRACKLE_COLORS.navy }}
                aria-hidden="true"
              />
            ) : (
              <UserIcon
                className="w-5 h-5"
                style={{ color: GRACKLE_COLORS.navy }}
                aria-hidden="true"
              />
            )}
            {user.name || 'User Details'}
          </DialogTitle>
        </DialogHeader>

        <div id="user-description" className="space-y-6">
          {/* Status and Role */}
          <div className="flex items-center gap-2">
            <Badge
              variant={user.is_active ? 'success' : 'secondary'}
              style={
                user.is_active
                  ? {
                      backgroundColor: SEMANTIC.success.fill,
                      color: SEMANTIC.success.text,
                      borderColor: SEMANTIC.success.fill,
                    }
                  : {
                      backgroundColor: SEMANTIC.neutral.fill,
                      color: SEMANTIC.neutral.text,
                      borderColor: SEMANTIC.neutral.fill,
                    }
              }
            >
              {user.is_active ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                  Active
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                  Inactive
                </>
              )}
            </Badge>
            <Badge
              style={{
                backgroundColor: getRoleBadgeColor(user.user_type).tint,
                color: GRACKLE_COLORS.prussianBlue,
                borderColor: getRoleBadgeColor(user.user_type).tint,
              }}
            >
              {getUserTypeDisplayName(user.user_type)}
            </Badge>
          </div>

          {/* Basic Information */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Basic Information
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-muted-foreground flex items-center gap-1">
                  <UserIcon className="w-4 h-4" aria-hidden="true" />
                  Full Name
                </dt>
                <dd className="text-sm font-medium mt-1">{user.name || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground flex items-center gap-1">
                  <Hash className="w-4 h-4" aria-hidden="true" />
                  Username
                </dt>
                <dd className="text-sm font-medium mt-1">{user.username || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground flex items-center gap-1">
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  Email
                </dt>
                <dd className="text-sm font-medium mt-1">
                  <a href={`mailto:${user.email}`} className="text-primary hover:underline">
                    {user.email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground flex items-center gap-1">
                  <Hash className="w-4 h-4" aria-hidden="true" />
                  Alias
                </dt>
                <dd className="text-sm font-medium mt-1">{user.alias || '-'}</dd>
              </div>
            </dl>
          </section>

          {/* Contact Information */}
          {(user.phone || user.mobile_phone || user.title || user.department) && (
            <section>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
                Contact Information
              </h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {user.title && (
                  <div>
                    <dt className="text-sm text-muted-foreground flex items-center gap-1">
                      <Briefcase className="w-4 h-4" aria-hidden="true" />
                      Title
                    </dt>
                    <dd className="text-sm font-medium mt-1">{user.title}</dd>
                  </div>
                )}
                {user.department && (
                  <div>
                    <dt className="text-sm text-muted-foreground flex items-center gap-1">
                      <Building className="w-4 h-4" aria-hidden="true" />
                      Department
                    </dt>
                    <dd className="text-sm font-medium mt-1">{user.department}</dd>
                  </div>
                )}
                {user.phone && (
                  <div>
                    <dt className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="w-4 h-4" aria-hidden="true" />
                      Phone
                    </dt>
                    <dd className="text-sm font-medium mt-1">
                      <a href={`tel:${user.phone}`} className="text-primary hover:underline">
                        {user.phone}
                      </a>
                    </dd>
                  </div>
                )}
                {user.mobile_phone && (
                  <div>
                    <dt className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="w-4 h-4" aria-hidden="true" />
                      Mobile
                    </dt>
                    <dd className="text-sm font-medium mt-1">
                      <a href={`tel:${user.mobile_phone}`} className="text-primary hover:underline">
                        {user.mobile_phone}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          {/* Manager Information */}
          {user.manager_name && (
            <section>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
                Reporting
              </h3>
              <dl className="grid grid-cols-1 gap-4">
                <div>
                  <dt className="text-sm text-muted-foreground flex items-center gap-1">
                    <Users className="w-4 h-4" aria-hidden="true" />
                    Manager
                  </dt>
                  <dd className="text-sm font-medium mt-1">{user.manager_name}</dd>
                </div>
              </dl>
            </section>
          )}

          {/* Ownership Statistics */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Owned Records
            </h3>
            <div
              className="grid grid-cols-2 sm:grid-cols-5 gap-4"
              role="group"
              aria-label="Owned record counts and forecasted revenue"
            >
              {user.accounts_owned !== undefined && (
                <div
                  className="text-center p-3 rounded-lg"
                  style={{
                    backgroundColor: CATEGORICAL.prussian.tint,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                  aria-label={`${user.accounts_owned} Accounts`}
                >
                  <div className="text-2xl font-bold" aria-hidden="true">
                    {user.accounts_owned}
                  </div>
                  <div className="text-xs mt-1" aria-hidden="true">
                    Accounts
                  </div>
                </div>
              )}
              {user.contacts_owned !== undefined && (
                <div
                  className="text-center p-3 rounded-lg"
                  style={{
                    backgroundColor: CATEGORICAL.navy.tint,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                  aria-label={`${user.contacts_owned} Contacts`}
                >
                  <div className="text-2xl font-bold" aria-hidden="true">
                    {user.contacts_owned}
                  </div>
                  <div className="text-xs mt-1" aria-hidden="true">
                    Contacts
                  </div>
                </div>
              )}
              {user.leads_owned !== undefined && (
                <div
                  className="text-center p-3 rounded-lg"
                  style={{
                    backgroundColor: CATEGORICAL.pollen.tint,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                  aria-label={`${user.leads_owned} Leads`}
                >
                  <div className="text-2xl font-bold" aria-hidden="true">
                    {user.leads_owned}
                  </div>
                  <div className="text-xs mt-1" aria-hidden="true">
                    Leads
                  </div>
                </div>
              )}
              {user.opportunities_owned !== undefined && (
                <div
                  className="text-center p-3 rounded-lg"
                  style={{
                    backgroundColor: CATEGORICAL.evergreen.tint,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                  aria-label={`${user.opportunities_owned} Opportunities`}
                >
                  <div className="text-2xl font-bold" aria-hidden="true">
                    {user.opportunities_owned}
                  </div>
                  <div className="text-xs mt-1" aria-hidden="true">
                    Opportunities
                  </div>
                </div>
              )}
              {user.tasks_owned !== undefined && (
                <div
                  className="text-center p-3 rounded-lg"
                  style={{
                    backgroundColor: CATEGORICAL.navy.tint,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                  aria-label={`${user.tasks_owned} Tasks`}
                >
                  <div className="text-2xl font-bold" aria-hidden="true">
                    {user.tasks_owned}
                  </div>
                  <div className="text-xs mt-1" aria-hidden="true">
                    Tasks
                  </div>
                </div>
              )}
              {user.forecasted_revenue !== undefined && (
                <div
                  className="text-center p-3 rounded-lg col-span-2 sm:col-span-5"
                  style={{
                    backgroundColor: SEMANTIC.success.fill,
                    color: SEMANTIC.success.text,
                  }}
                  aria-label={`Forecasted Revenue: $${Number(user.forecasted_revenue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                >
                  <div className="text-2xl font-bold" aria-hidden="true">
                    $
                    {Number(user.forecasted_revenue).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <div className="text-xs mt-1" aria-hidden="true">
                    Forecasted Revenue (FY{' '}
                    {new Date().getMonth() >= 2
                      ? new Date().getFullYear()
                      : new Date().getFullYear() - 1}
                    /
                    {(new Date().getMonth() >= 2
                      ? new Date().getFullYear() + 1
                      : new Date().getFullYear()
                    )
                      .toString()
                      .slice(-2)}
                    )
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* System Information */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              System Information
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-4 h-4" aria-hidden="true" />
                  Created Date
                </dt>
                <dd className="text-sm font-medium mt-1">{formatDate(user.created_date)}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="w-4 h-4" aria-hidden="true" />
                  Last Login
                </dt>
                <dd className="text-sm font-medium mt-1">
                  {user.last_login ? formatDate(user.last_login) : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground flex items-center gap-1">
                  <Hash className="w-4 h-4" aria-hidden="true" />
                  User ID
                </dt>
                <dd className="text-sm font-medium mt-1 font-mono">{user.id}</dd>
              </div>
            </dl>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
